/**
 * Client-side signature types.
 *
 * The canonical schema lives in {@link ./sharedSchema} (a mirror of the
 * server's `server/types/sharedSchema.ts`). Almost everything here is just a
 * re-export from there — keeping a separate file means consumers can `import
 * { ... } from "@types/signatures"` without thinking about whether the symbol
 * is signature-specific or part of the broader schema.
 *
 * The handful of LOCAL definitions below are types whose runtime shape on
 * the client genuinely differs from the server / Firestore shape — namely,
 * Firestore Date fields arrive on the client as ISO strings (after
 * REST round-trips or `normalizeFirestoreTimestamps` in services/signatures.ts).
 * We override those specific fields with `string` so `.localeCompare` and
 * similar ops stay type-safe at consumer sites without casting.
 */

import type {
  SignatureTemplate as SharedSignatureTemplate,
  SignatureDocument as SharedSignatureDocument,
  SignatureDocumentSigner as SharedSignatureDocumentSigner,
  SignatureWhatsappDelivery as SharedSignatureWhatsappDelivery,
  SignatureTemplateBundle as SharedSignatureTemplateBundle
} from "./sharedSchema";

// Pure re-exports — identical shape on client and server.
export type {
  SignaturePrefillSource,
  SignatureFieldType,
  SignatureFieldFillMode,
  SignatureDateFormat,
  SignatureTemplateField,
  SignaturePriceFieldConfig,
  SignatureRadioFieldConfig,
  SignatureRadioOption,
  SignatureNameTag,
  SignatureNameTagColor,
  SignatureFieldPosition,
  SignatureSignerRole,
  SignatureTemplateContent,
  SignatureSignerStatus,
  SignatureWhatsappDeliveryStatus,
  SignatureDocumentStatus,
  SignatureTemplateVisibility,
  ListingsBlock,
  ListingsBlockColumn,
  ListingsBlockListingFilter,
  ListingsBlockFormat,
  AdditionalTermsBlock,
  UserInfoBlockLineKey,
  UserInfoBlockLineConfig,
  UserInfoBlockConfig,
  SellerQuestionnaireQuestionDef,
  SellerQuestionnaireAnswerOption,
  SellerQuestionnaireQuestionAnswer,
  SellerQuestionnaireRightType,
  SellerQuestionnaireRegistrationType,
  SellerQuestionnairePropertyDetails,
  SellerQuestionnaireRights,
  SellerQuestionnaireOwnerDetails,
  SellerQuestionnaireRealtorDetails,
  SellerQuestionnaireAnswers,
  SignatureSellerQuestionnaire
} from "./sharedSchema";

export {
  SignaturePrefillSources,
  SignatureFieldTypes,
  SignatureFieldFillModes,
  SignatureNameTagColors,
  SIGNATURE_NAME_TAG_MAX_LENGTH,
  DEFAULT_SIGNATURE_NAME_TAG_COLOR,
  SignatureDateFormats,
  DEFAULT_SIGNATURE_DATE_FORMAT,
  DEFAULT_SIGNATURE_FIELD_FONT_SIZE,
  DEFAULT_SIGNATURE_FIELD_ALIGN,
  DEFAULT_FIELD_SUFFIX_BY_TYPE,
  DEFAULT_PRICE_PERCENT_SUFFIX,
  PRICE_ONE_MONTH_RENT_LABEL,
  SIGNATURE_FIELD_AFFIX_WIDTH_PX,
  SignatureSignerStatuses,
  SignatureWhatsappDeliveryStatuses,
  SignatureDocumentStatuses,
  SignatureTemplateVisibilities,
  DEFAULT_SIGNATURE_TEMPLATE_VISIBILITY,
  canSetSignatureVisibility,
  isSignatureEmailField,
  isSignatureFieldRequiredForSigner,
  SIGNATURE_SYSTEM_TEMPLATE_OWNER,
  ListingsBlockFormats,
  ListingsBlockColumns,
  ListingsBlockListingFilters,
  DEFAULT_LISTINGS_BLOCK_LISTING_FILTER,
  DEFAULT_LISTINGS_BLOCK_FONT_SIZE,
  DEFAULT_LISTINGS_BLOCK_COLUMNS,
  DEFAULT_HIDDEN_ADDRESS_CONSENT_LINES,
  DEFAULT_ADDITIONAL_TERMS_TITLE,
  SELLER_QUESTIONNAIRE_VERSION,
  SELLER_QUESTIONNAIRE_QUESTIONS,
  SellerQuestionnaireAnswerOptions,
  SellerQuestionnaireRightTypes,
  SellerQuestionnaireRegistrationTypes,
  LISTINGS_BLOCK_COLUMN_WEIGHTS,
  USER_INFO_BLOCK_LINE_KEYS,
  // The shared name is `effectiveSignatureFillMode`; we re-export under the
  // shorter `effectiveFillMode` for ergonomic call sites in the signatures
  // feature, where the surrounding context already says "signature".
  effectiveSignatureFillMode as effectiveFillMode
} from "./sharedSchema";

// === Client-only views with date fields typed as strings ===
//
// These four types are the ONLY substantive divergence from the shared
// schema. The server's BaseDocument + signature-document types use `Date` for
// timestamps; the client receives ISO strings from REST endpoints and from
// `normalizeFirestoreTimestamps` in services/signatures.ts. Defining the
// overrides here keeps consumers' string-ops (e.g. `.localeCompare`) honest
// without per-call-site casts.

export type SignatureWhatsappDelivery = Omit<SharedSignatureWhatsappDelivery, "lastAttemptAt"> & {
  lastAttemptAt?: string;
};

export type SignatureDocumentSigner = Omit<
  SharedSignatureDocumentSigner,
  | "sentAt"
  | "viewedAt"
  | "signedAt"
  | "declinedAt"
  | "mainSignedAt"
  | "addendumSignedAt"
  | "questionnaireSignedAt"
  | "otpVerifiedAt"
  | "whatsappDelivery"
> & {
  sentAt?: string;
  viewedAt?: string;
  signedAt?: string;
  declinedAt?: string;
  mainSignedAt?: string;
  addendumSignedAt?: string;
  questionnaireSignedAt?: string;
  otpVerifiedAt?: string;
  whatsappDelivery: SignatureWhatsappDelivery;
};

export type SignatureTemplate = Omit<
  SharedSignatureTemplate,
  "id" | "createdTime" | "updatedTime" | "archivedAt"
> & {
  // `id` is always present on the client (Firestore doc id, set by the
  // converter), unlike the server schema where it's part of `Partial<BaseDocument>`.
  id: string;
  createdTime?: string;
  updatedTime?: string;
  archivedAt?: string;
};

export type SignatureDocument = Omit<
  SharedSignatureDocument,
  "id" | "createdTime" | "updatedTime" | "sentAt" | "completedAt" | "archivedAt" | "editedAt" | "signers"
> & {
  id: string;
  createdTime?: string;
  updatedTime?: string;
  sentAt?: string;
  completedAt?: string;
  archivedAt?: string;
  editedAt?: string;
  signers: SignatureDocumentSigner[];
};

/**
 * Client-side view of a {@link SharedSignatureTemplateBundle}. Same dates-as-
 * strings divergence as {@link SignatureTemplate}.
 */
export type SignatureTemplateBundle = Omit<
  SharedSignatureTemplateBundle,
  "id" | "createdTime" | "updatedTime" | "archivedAt"
> & {
  id: string;
  createdTime?: string;
  updatedTime?: string;
  archivedAt?: string;
};

// === REST input shapes ===
//
// Distinct from `SignatureDocumentSigner` — no Firestore-only fields, all
// values are client-provided. Lives only on the client, so there's nothing
// to dedupe against the shared schema.

export type SignerDraftInput = {
  roleId: string;
  contactId?: string;
  name: string;
  phone: string;
  email?: string;
  fieldValues?: Record<string, string>;
};
